import {
  Deployment,
  TrajectoryInfo,
  TemperatureData,
  SnowData,
  ContactFormData,
  UserData,
  Sensor,
  SensorCalibration,
} from '../types/types';

import { API_BASE_URL } from '../constants/config';

export const fetchDeployments = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/deployments`);
    if (!response.ok) {
      throw new Error('Failed to fetch deployments');
    }
    return await response.json();
  } catch (error) {
    console.error('Error while retrieving deployments:', error);
    throw error;
  }
};

export const fetchDeployment = async (
  deploymentId: number
): Promise<Deployment> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/deployments/${deploymentId}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch deployment data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching deployment data:', error);
    throw error;
  }
};

export const fetchTrajectories = async (
  deploymentId: string
): Promise<any[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/trajectories/${deploymentId}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch trajectory data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching trajectory data:', error);
    throw error;
  }
};

export const fetchTrajectoryFiles = async (deploymentId: string) => {
  const res = await fetch(
    `${API_BASE_URL}/api/model/trajectories-list/${deploymentId}`
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Server error: ${text}`);
  }
  return res.json();
};

export const fetchTrajectoryDetails = async (
  deploymentNumber: number,
  color: string
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/deployments/${deploymentNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch trajectory details');
    }
    const data = await response.json();
    return data.trajectories.find(
      (trajectory: { color: string }) => trajectory.color === color
    );
  } catch (error) {
    console.error('Error while retrieving trajectory details:', error);
    throw error;
  }
};

export const fetchDeploymentInfo = async (
  deploymentId: number,
  colorKey: string
) => {
  const res = await fetch(`${API_BASE_URL}/api/deployments/${deploymentId}`);
  if (!res.ok) {
    throw new Error('Failed to fetch deployment data');
  }
  const data = await res.json();
  return data.trajectories.find(
    (trajectory: TrajectoryInfo) => trajectory.color === colorKey
  );
};

export const fetchSnowDepthData = async (): Promise<SnowData[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/snow-depth`);
    if (!response.ok) {
      throw new Error('Failed to fetch snow depth data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching snow depth data:', error);
    throw error;
  }
};

export const fetchTemperatureData = async (): Promise<TemperatureData[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/avg-temp`);
    if (!response.ok) {
      throw new Error('Failed to fetch temperature data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching temperature data:', error);
    throw error;
  }
};

export const fetchStatistics = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching statistics:', error);
    throw error;
  }
};

export const fetchPosts = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/posts`);
    if (!res.ok) {
      throw new Error('Failed to fetch posts');
    }
    const rawPosts = await res.json();
    return rawPosts;
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
};

export const fetchPostsHomePage = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/posts`);
    if (!res.ok) throw new Error('Failed to fetch posts');
    const rawPosts = await res.json();
    return rawPosts.map((post: any) => ({
      title: post.title,
      date: post.date,
      id: post.id,
    }));
  } catch (error) {
    console.error('Error loading posts:', error);
    throw error;
  }
};

export const fetchVideos = async (selected: { [key: string]: string[] }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ selected }),
    });
    if (!response.ok) throw new Error('Failed to fetch videos');
    return await response.json();
  } catch (error) {
    console.error('Error fetching videos:', error);
    throw error;
  }
};

export const fetchTeaseVideos = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/tease_videos`);
    if (!response.ok) throw new Error('Failed to fetch tease videos');
    return await response.json();
  } catch (error) {
    console.error('Error fetching tease videos:', error);
    throw error;
  }
};

export const signin = async (email: string, password: string) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    return data.token;
  } catch (err: unknown) {
    if (err instanceof Error) {
      throw new Error(err.message);
    } else {
      throw new Error('network error');
    }
  }
};

export const signup = async (email: string, password: string) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Signup failed');
    }

    return data;
  } catch (err: unknown) {
    if (err instanceof Error) {
      throw new Error(err.message || 'Network error');
    }
  }
};

export const verifyEmail = async (token: string) => {
  const res = await fetch(
    `${API_BASE_URL}/api/verify-email?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Verification failed');
  return data;
};

export const sendContactForm = async (formData: ContactFormData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || `Server error (${res.status})`);
    }

    return await res.json();
  } catch (error) {
    console.error('Error sending contact message:', error);
    throw error;
  }
};

export const getDownload = (
  selected: { [key: string]: string[] },
  email?: string,
  format?: string
): string => {
  const cleanedSelected = {
    ...selected,
    Deployment: (selected.Deployment || []).filter(
      (d) => !d.startsWith('Deployment')
    ),
  };

  const params = new URLSearchParams();
  params.set('selected', JSON.stringify(cleanedSelected));

  if (email) {
    params.set('email', email);
  }

  if (format) {
    params.set('format', format);
  }

  return `${API_BASE_URL}/api/download?${params.toString()}`;
};

export const fetchUser = async (token: string | null): Promise<UserData> => {
  const res = await fetch(`${API_BASE_URL}/api/profile`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errorMessage = 'Failed to fetch user';
    try {
      const data = await res.json();
      if (data.error) errorMessage = data.error;
    } catch {}
    throw new Error(errorMessage);
  }

  const data = await res.json();
  console.log(data);
  return { email: data.email };
};

export const fetchDownloads = async (token: string, email?: string) => {
  const params = new URLSearchParams();
  if (email) params.set('email', email);

  const res = await fetch(
    `${API_BASE_URL}/api/profile/downloads?${params.toString()}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    let errorMessage = 'Failed to fetch downloads';
    try {
      const data = await res.json();
      if (data.error) errorMessage = data.error;
    } catch {}
    throw new Error(errorMessage);
  }

  return res.json();
};

export const deleteAccount = async (token: string) => {
  const res = await fetch(`${API_BASE_URL}/api/delete`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errorMessage = 'Failed to delete account';
    try {
      const data = await res.json();
      if (data.error) errorMessage = data.error;
    } catch {}
    throw new Error(errorMessage);
  }

  return true;
};

export const requestPasswordReset = async (email: string) => {
  const res = await fetch(`${API_BASE_URL}/api/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Failed to request password reset');
  }

  return data;
};

export const resetPassword = async (token: string, password: string) => {
  const res = await fetch(`${API_BASE_URL}/api/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Failed to reset password');
  }

  return data;
};

export const fetchFilteredDeployments = async (selected: any) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/deployments/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selected),
    });

    if (!res.ok) {
      throw new Error('Failed to fetch filtered deployments');
    }

    const data = await res.json();
    // return data.map((d: any) => d.date);
    return data;
  } catch (err) {
    console.error('Error fetching filtered deployments:', err);
    return [];
  }
};

export async function fetchSensors(): Promise<Sensor[]> {
  const res = await fetch(`${API_BASE_URL}/api/sensors`);
  if (!res.ok) {
    throw new Error(`HTTP error! status: ${res.status}`);
  }
  return res.json();
}

export async function fetchSensorCalibration(): Promise<SensorCalibration[]> {
  const response = await fetch(`${API_BASE_URL}/api/sensorCalibration`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export function fetchSensorCalibrationZip(sensorName: string) {
  if (sensorName === 'none') {
    throw new Error(`Downloading is not yet enabled for ${sensorName}`);
  }

  const url = `${API_BASE_URL}/api/sensorCalibration/download/${sensorName}`;
  const link = document.createElement('a');

  link.href = url;
  const baseName = sensorName.replace(/\.zip$/i, '');
  link.download = `${baseName}_calibration.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function fetchMeteoDataZip() {
  const url = `${API_BASE_URL}/api/meteo_data/download`;

  const link = document.createElement('a');
  link.href = url;
  link.download = `meteo_data.zip`;

  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function fetchGnssDataZip() {
  const url = `${API_BASE_URL}/api/gnss_data/download`;

  const link = document.createElement('a');
  link.href = url;
  link.download = `gnss_data.zip`;

  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchDataDownloadExample(season: string) {
  if (season !== 'winter' && season !== 'summer') {
    throw new Error(`Unknown season: ${season}`);
  }

  const url = `${API_BASE_URL}/api/download/data-example/${season}`;

  const link = document.createElement('a');
  link.href = url;
  link.download = `${season}_example.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export const fetchDeploymentLogs = async () => {
  const res = await fetch(`${API_BASE_URL}/api/deploymentLogs`);
  if (!res.ok) throw new Error('Failed to fetch deployment logs');
  return res.json();
};

export const fetchEstimate = async (selected: {
  [key: string]: string[];
}): Promise<{ bytes: number; human: string }> => {
  const cleanedSelected = {
    ...selected,
    Deployment: (selected.Deployment || []).filter(
      (d) => !d.startsWith('Deployment')
    ),
  };
  const params = new URLSearchParams();
  params.set('selected', JSON.stringify(cleanedSelected));
  const res = await fetch(`${API_BASE_URL}/api/estimate?${params.toString()}`);
  if (!res.ok) throw new Error('Estimate failed');
  return res.json();
};
